import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { MessageSquare, TrendingUp, Clock, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/reports/overview`, { withCredentials: true }),
      axios.get(`${API}/admin/reports/timeline?days=30`, { withCredentials: true }),
      axios.get(`${API}/admin/reports/top-companies`, { withCredentials: true }),
    ])
      .then(([ov, tl, co]) => {
        setOverview(ov.data);
        setTimeline(tl.data.timeline);
        setCompanies(co.data.companies);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-slate-400">Loading dashboard...</div>;
  }

  const stats = [
    { label: 'Total Inquiries', value: overview?.total_submissions || 0, icon: MessageSquare, color: 'blue' },
    { label: 'This Month', value: overview?.this_month || 0, icon: TrendingUp, color: 'emerald' },
    { label: 'This Week', value: overview?.this_week || 0, icon: Clock, color: 'amber' },
    { label: 'Companies', value: companies.length, icon: Building2, color: 'purple' },
  ];

  const colorMap = { blue: 'bg-blue-500/10 text-blue-400', emerald: 'bg-emerald-500/10 text-emerald-400', amber: 'bg-amber-500/10 text-amber-400', purple: 'bg-purple-500/10 text-purple-400' };

  const statusData = overview?.status_breakdown || {};
  const statusChart = Object.entries(statusData).map(([name, count]) => ({ name, count }));

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-white mb-8" data-testid="dashboard-title">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-slate-800 rounded-xl p-5 border border-slate-700" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">{label}</span>
              <div className={`p-2 rounded-lg ${colorMap[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <div className="text-3xl font-bold text-white">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Timeline Chart */}
        <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="timeline-chart">
          <h2 className="text-lg font-semibold text-white mb-4">Submissions (Last 30 Days)</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="fillBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="url(#fillBlue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Breakdown + Top Companies */}
        <div className="space-y-6">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="status-chart">
            <h2 className="text-lg font-semibold text-white mb-4">By Status</h2>
            {statusChart.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} width={80} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No data yet</p>
            )}
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700" data-testid="top-companies">
            <h2 className="text-lg font-semibold text-white mb-4">Top Companies</h2>
            {companies.length > 0 ? (
              <div className="space-y-3">
                {companies.slice(0, 5).map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 truncate mr-2">{c.name}</span>
                    <span className="text-blue-400 font-medium">{c.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No company data yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
